<template>
  <div class="card card-confirm">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h6 class="mb-0">{{ $t("paymentpending.title_modal") }}</h6>
      <button 
        :disabled="imageLoading || buttonActive || showConfirmation"
        :class="['btn-close-custom', { 'loading': imageLoading, 'active': buttonActive || showConfirmation }]"
        type="button" 
        data-bs-dismiss="modal" 
        aria-label="Close"
        @click="closeModal"
        style="width: 50px; height: 50px; cursor: pointer;">
        
        <!-- Spinner de loading -->
        <div v-if="imageLoading" class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">{{ $t("paymentpending.loading") }}</span>
        </div>
        
        <!-- Icône croix -->
        <i v-else class="fa fa-times"></i>
        
        <!-- Texte caché pour l'accessibilité -->
        <span class="sr-only">{{ $t("button.cancel") }}</span>
      </button>
    </div>
    <div class="card-body">
      <div class="row" v-if="selectedPayment">
        <!-- Section des informations -->
        <div class="col-md-5">
          <div class="payment-info">
            <div class="info-item">
              <label class="info-label">{{ $t("paymentpending.matricul") }}</label>
              <span class="info-value">{{ selectedPayment.registration_number }}</span>
            </div>
            <div class="info-item">
              <label class="info-label">{{ $t("paymentpending.name") }}</label>
              <span class="info-value">{{ selectedPayment.last_name }}</span>
            </div>
            <div class="info-item">
              <label class="info-label">{{ $t("paymentpending.last_name") }}</label>
              <span class="info-value">{{ selectedPayment.first_name }}</span>
            </div>
            <div class="info-item">
              <label class="info-label">{{ $t("paymentpending.grade") }}</label>
              <span class="info-value">
                {{ selectedPayment.classroom_grade }} -
                {{ selectedPayment.classroom_speciality }}
                {{ selectedPayment.classroom_number }}
              </span>
            </div>
            
            <div class="info-item amount-item">
              <label class="info-label">{{ $t("paymentpending.amount") }}</label>
              <span class="info-value amount-value">
                {{currency_code}} 
                {{ Number(selectedPayment.amount_paid).toLocaleString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        }) }}
              </span>
            </div>
          </div>
        </div>
        
        <!-- Section de l'image améliorée -->
        <div class="col-md-7">
          <div v-if="imageLoading" class="d-flex justify-content-center align-items-center" style="height: 380px;">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">{{ $t("paymentpending.loading") }}</span>
            </div>
          </div>
          <div class="image-section" v-else>
            <div class="image-container" @click="openImageModal">
              <img 
                class="receipt-image" 
                :src="selectedPayment.payment.receipt_image_url" 
                :alt="$t('paymentpending.payment_receipt')"
              >
              <div class="image-overlay">
                <div class="view-button-container">
                  <button class="view-button" :title="$t('paymentpending.click_to_enlarge')">
                    <i class="fa fa-search-plus"></i>
                  </button>
                  <span class="view-text">{{ $t("paymentpending.click_to_enlarge") }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer mt-3">
      <div class="row">
        <div class="col-lg-6 d-flex">
          
        </div>
        <div class="col-lg-6 d-flex justify-content-end">
          <argon-button :loading="loading" :active="buttonActive || showConfirmation" color="danger"
            :title="$t('paymentpending.reject_title')" type="button" class="mt-1" @click="deletePayment"
            style="width: 150px; height: 50px; padding-top: 15px; cursor: pointer; margin-right: 10px;">
            {{ $t("button.reject") }}
          </argon-button>
          <argon-button :loading="loading" :active="buttonActive || showConfirmation" color="success"
            :title="$t('paymentpending.confirm_title')" type="button" class="mt-1" @click="confirmPayment"
            style="width: 150px; height: 50px; padding-top: 15px; cursor: pointer;">
            {{ $t("button.continue") }}
          </argon-button>
        </div>
      </div>
    </div>

    <!-- Modal de visualisation d'image optimisée -->
    <div v-if="showImageModal" class="image-modal-overlay" @click="closeImageModal">
      <div class="image-modal" @click.stop>
        <div class="image-modal-header">
          <h5 class="modal-title">{{ $t("paymentpending.modal_image_title", [selectedPayment.registration_number]) }}</h5>
          <button class="modal-close-btn" @click="closeImageModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        
        <div class="image-modal-body">
          <div class="image-wrapper">
              <img 
                ref="modalImage"
                class="modal-image" 
                :class="{ 'image-zoomed': isZoomed }"
                :style="imageStyle"
                :src="selectedPayment.payment.receipt_image_url"  
                :alt="$t('paymentpending.payment_receipt')"
                @mousedown="startDrag"
                @mousemove="drag"
                @mouseup="endDrag"
                @mouseleave="endDrag"
              >
          </div>
        </div> 
        
        <div class="image-modal-footer">
          <div class="modal-controls">
            <!-- Bouton Rotation -->
            <button class="control-btn rotate-btn" :title="$t('paymentpending.rotation_title')" @click="rotateImage">
              <i class="fa fa-rotate-right"></i>
              <span>{{ $t("paymentpending.rotation") }}</span>
            </button>
            
            <!-- Bouton Zoom -->
            <button class="control-btn zoom-btn" :title="$t('paymentpending.zoom')" @click="toggleZoom">
              <i :class="isZoomed ? 'fa fa-search-minus' : 'fa fa-search-plus'"></i>
              <span>{{ isZoomed ? $t('paymentpending.zoom_out') : $t('paymentpending.zoom_in') }}</span>
            </button>
            
            <!-- Bouton Nouvelle fenêtre -->
            <button class="control-btn new-window-btn" :title="$t('paymentpending.new_window_title')">
              <a target="_blank" :href="selectedPayment.payment.receipt_image_url">
                <i class="fa fa-external-link-alt"></i>
                <span>{{ $t("paymentpending.new_window") }}</span>
              </a>
            </button>
            
            <!-- Bouton Imprimer -->
            <button class="control-btn print-btn" :title="$t('paymentpending.print_title')" @click="printImage">
              <i class="fa fa-print"></i>
              <span>{{ $t("paymentpending.print") }}</span>
            </button>
            
            <!-- Bouton Télécharger -->
            <button class="control-btn download-btn" :title="$t('paymentpending.download_title')">
              <a :href="selectedPayment.payment.receipt_image_url" :download="`recu-${selectedPayment.registration_number}.jpg`">
                <i class="fa fa-download"></i>
                <span>{{ $t("paymentpending.download") }}</span>
              </a>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import api_config from "@/config";

export default {
  name: "Payment-Pending-Detail",
  
  data(){
    return{
      imageLoading: false,
      currency_code: api_config.CURRENCY_CODE,
      currentRotation: 0,
      isZoomed: false,
      showImageModal: false,
      zoomLevel: 1,
      dragStart: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 },
      isDragging: false,
      imagePosition: { x: 0, y: 0 }
    }
  },

  components: {
    ArgonButton,
  },

  computed: {
    imageStyle() {
      return {
        transform: `translate(${this.imagePosition.x}px, ${this.imagePosition.y}px) rotate(${this.currentRotation}deg) scale(${this.zoomLevel})`,
        transformOrigin: 'center center',
        transition: this.isDragging ? 'none' : 'transform 0.3s ease'
      }
    }
  },

  methods: {
    deletePayment() {
      this.$emit("delete");
    },
    confirmPayment() {
      this.$emit("continue");
    },
    closeModal() {
      this.$emit("cancel");
    },
    
    // Nouvelles méthodes pour la modal d'image optimisée
    openImageModal() {
      this.showImageModal = true;
      this.resetImage();
      // Empêcher le scroll du body
      document.body.style.overflow = 'hidden';
    },
    
    closeImageModal() {
      this.showImageModal = false;
      this.resetImage();
      // Restaurer le scroll du body
      document.body.style.overflow = 'auto';
    },
    
    rotateImage() {
      this.currentRotation = (this.currentRotation + 90) % 360;
      this.imagePosition = { x: 0, y: 0 }; // Reset position après rotation
    },
    
    toggleZoom() {
      if (this.isZoomed) {
        this.zoomOut();
      } else {
        this.zoomIn();
      }
    },

    zoomIn() {
      this.isZoomed = true;
      this.zoomLevel = 2;
      this.imagePosition = { x: 0, y: 0 }; // Centrer l'image zoomée
    },

    zoomOut() {
      this.isZoomed = false;
      this.zoomLevel = 1;
      this.imagePosition = { x: 0, y: 0 };
    },

    resetImage() {
      this.currentRotation = 0;
      this.isZoomed = false;
      this.zoomLevel = 1;
      this.imagePosition = { x: 0, y: 0 };
      this.isDragging = false;
    },

    // Gestion du drag pour l'image zoomée
    startDrag(event) {
      if (!this.isZoomed) return;
      
      this.isDragging = true;
      this.dragStart = {
        x: event.clientX - this.imagePosition.x,
        y: event.clientY - this.imagePosition.y
      };
      event.preventDefault();
    },

    drag(event) {
      if (!this.isDragging || !this.isZoomed) return;
      
      this.imagePosition = {
        x: event.clientX - this.dragStart.x,
        y: event.clientY - this.dragStart.y
      };
      event.preventDefault();
    },

    endDrag() {
      this.isDragging = false;
    },
    
    printImage() {
      const imageUrl = this.selectedPayment.payment.receipt_image_url;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Reçu de paiement - ${this.selectedPayment.registration_number}</title>
            <style>
              body { 
                margin: 0; 
                padding: 20px;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              img { 
                max-width: 100%; 
                max-height: 100vh;
                object-fit: contain;
              }
              @media print {
                body { padding: 0; }
                img { 
                  width: 100%;
                  height: auto;
                }
              }
            </style>
          </head>
          <body>
            <img src="${imageUrl}" alt="Reçu de paiement" onload="window.print(); window.close();">
          </body>
        </html>
      `);
      printWindow.document.close();
    },
    
    handleKeydown(event) {
      if (event.key === 'Escape' && this.showImageModal) {
        this.closeImageModal();
      }
    }
  },

  // Gérer la fermeture avec Escape
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    // S'assurer que le scroll est restauré
    document.body.style.overflow = 'auto';
  },

  props: {
    selectedPayment: Object,
    payment: {
      type: String,
    },
    name: {
      type: String,
    },
    grade: {
      type: String,
    },
    amount: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false
    },
    buttonActive: {
      type: Boolean,
      default: false
    },
    showConfirmation: {
      type: Boolean,
      default: false
    },
  },
};
</script>

<style scoped>
/* Styles existants conservés */
.card-header {
  padding: 15px 20px;
}

.btn-close-custom {
  background-color: transparent;
  padding: 0px 10px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: #ffffff;
  font-size: 25px;
}

.btn-close-custom:hover:not(:disabled) {
  transform: scale(1.05);
}

.btn-close-custom:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-close-custom.loading {
  background: #ffc107;
  opacity: 0.8;
}

.btn-close-custom.active {
  background: #e0a800;
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.5);
}

.btn-close-custom .spinner-border-sm {
  width: 1rem;
  height: 1rem;
  color: #212529;
}

.payment-info {
  padding: 40px 0;
}

.info-item {
  margin-bottom: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-item:hover {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding-left: 12px;
  padding-right: 12px;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0;
  flex-shrink: 0;
}

.info-value {
  font-size: 1rem;
  font-weight: 500;
  color: #ffffff;
  line-height: 1.4;
  text-align: right;
  margin-left: 16px;
}

.amount-item {
  background: linear-gradient(135deg, rgba(45, 206, 137, 0.1), rgba(45, 206, 137, 0.05));
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(45, 206, 137, 0.2);
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.amount-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2DCE89;
}

/* Styles pour la section image */
.image-section {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 380px;
  padding: 20px;
}

.image-container {
  position: relative;
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  cursor: pointer;
}

.image-container:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.25);
}

.receipt-image {
  height: 340px;
  object-fit: contain;
  display: block;
  transition: all 0.3s ease;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: all 0.3s ease;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.view-button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.view-button {
  background: #2DCE89;
  border: none;
  border-radius: 50%;
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(45, 206, 137, 0.4);
  cursor: pointer;
}

.view-button:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 25px rgba(45, 206, 137, 0.6);
}

.view-button i {
  color: white;
  font-size: 1.5rem;
}

.view-text {
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  opacity: 0.9;
}

/* Styles optimisés pour la modal d'image */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 20px;
}

.image-modal {
  position: relative;
  background: #1a1a1a;
  border-radius: 16px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.image-modal-header {
  padding: 20px 25px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #2a2a2a;
  flex-shrink: 0;
}

.modal-title {
  color: white;
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.modal-close-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.1);
}

.image-modal-body {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 70px;
  overflow: hidden;
  background: #222;
  min-height: 400px;
}

.image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
}

.modal-image {
  width: auto;
  height: auto;
  max-width: 95%;
  max-height: 95%;
  object-fit: contain;
  display: block;
  cursor: default;
  user-select: none;
  -webkit-user-drag: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.modal-image.image-zoomed {
  cursor: grab;
  max-width: none;
  max-height: none;
}

.modal-image.image-zoomed:active {
  cursor: grabbing;
}

.image-modal-footer {
  padding: 15px 25px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: #2a2a2a;
  flex-shrink: 0;
}

.modal-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.control-btn {
  background: #3a3a3a;
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 75px;
}

.control-btn:hover {
  background: #4a4a4a;
  transform: translateY(-2px);
}

.control-btn i {
  color: white;
  font-size: 1rem;
}

.control-btn span {
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.control-btn a {
  color: white;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.control-btn a:hover {
  color: white;
}

/* Couleurs spécifiques pour chaque bouton */
.rotate-btn:hover {
  background: #FB6340;
}

.zoom-btn:hover {
  background: #5E72E4;
}

.reset-btn:hover {
  background: #F5365C;
}

.new-window-btn:hover {
  background: #2DCE89;
}

.print-btn:hover {
  background: #11CDEF;
}

.download-btn:hover {
  background: #FBBE07;
}

/* Responsive optimisé */
@media (max-width: 768px) {
  .image-modal-overlay {
    padding: 15px;
  }
  
  .image-modal {
    max-width: 100%;
    max-height: 90vh;
    border-radius: 12px;
  }
  
  .image-modal-header,
  .image-modal-footer {
    padding: 15px 20px;
  }
  
  .image-modal-body {
    padding: 15px;
    min-height: 300px;
  }
  
  .modal-title {
    font-size: 1rem;
  }
  
  .modal-controls {
    gap: 8px;
  }
  
  .control-btn {
    min-width: 65px;
    padding: 8px 10px;
  }
  
  .control-btn i {
    font-size: 0.9rem;
  }
  
  .control-btn span {
    font-size: 0.7rem;
  }
  
  .view-button {
    width: 60px;
    height: 60px;
  }
  
  .view-button i {
    font-size: 1.3rem;
  }
  
  .view-text {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .image-modal-overlay {
    padding: 10px;
  }
  
  .image-modal {
    border-radius: 8px;
    max-height: 95vh;
  }
  
  .image-modal-body {
    padding: 10px;
    min-height: 250px;
  }
  
  .modal-controls {
    gap: 6px;
  }
  
  .control-btn {
    min-width: 55px;
    padding: 6px 8px;
  }
  
  .control-btn span {
    font-size: 0.65rem;
  }
  
  .modal-title {
    font-size: 0.9rem;
  }
}


@media (min-width: 481px) and (max-width: 1024px) {
  .image-modal {
    max-width: 700px;
  }
  
  .modal-controls {
    gap: 10px;
  }
  
  .control-btn {
    min-width: 70px;
    padding: 9px 12px;
  }
}
</style>